import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  changePatient,
  toggleModal,
} from "app/store/transmission/slice/transmissionSlice";

import usePatientTableColumns from "./hooks/usePatientTableColumn";
import { DEFAULT_LARGE_PAGE_SIZE_OPTIONS } from "static/pagination";
import CustomTable from "components/common/table/CustomTable";

import useDebounce from "app/hooks/useDebounce";
import PatientTableToolbar from "./components/PatientTableToolbar";
import { useGetPatientListQuery } from "app/store/patient/api/patientApi";
import {
  changeSearch,
  changeSelectedRows,
} from "app/store/patient/slice/patientSlice";
import PatientBulkActionModal from "pages/patients/PatientBulkActionModal";
import { USER_ROLES } from "static/constants";

const PatientsTable = () => {
  const dispatch = useDispatch();

  const {
    searchText,
    showPatientAlert,
    showDeviceAlert,
    showMissingContactInfo,
    showAlertWhenDisconnected,
  } = useSelector((state) => state.patient);

  const { columns } = usePatientTableColumns();

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);

  const { currentUser } = useSelector((state) => state.auth);
  const [columnVisibility, setColumnVisibility] = useState({});

  // An assigned user starts on their own hospitals, unassigned patients left out.
  // Both are theirs to change from the filter.
  useEffect(() => {
    if (currentUser?.isFollowingHospital !== true) {
      return;
    }

    const assigned = [
      ...new Set(
        (currentUser?.followingHospitals || [])
          .map((hospital) => String(hospital ?? "").trim())
          .filter(Boolean)
      ),
    ];

    if (!assigned.length) {
      return;
    }

    setColumnFilters((current) =>
      current.some((filter) => filter.id === "hospital")
        ? current
        : [...current, { id: "hospital", value: assigned }]
    );
  }, [currentUser]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const [rowSelection, setRowSelection] = useState({});
  const [search, setSearch] = useState("");

  const handleSortingChange = (nextSorting) => {
    setSorting(nextSorting);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const { data, isLoading, isFetching } = useGetPatientListQuery(
    {
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      search,
      filters: columnFilters,
      sorting,
      showPatientAlert,
      showDeviceAlert,
      showMissingContactInfo,
      showAlertWhenDisconnected,
    },
    { refetchOnMountOrArgChange: true }
  );

  const onSearchChange = useDebounce((value) => {
    setSearch(value || undefined);
    dispatch(changeSearch(value));
  }, 1600);

  const onRowClick = (rowData) => {
    dispatch(
      changePatient({
        patientId: rowData?._id,
        serialNumber: rowData?.serialNumber,
        account: rowData?.account,
      })
    );
    dispatch(toggleModal({ type: "patient", value: true }));
  };

  // Update localStorage whenever columnVisibility changes
  useEffect(() => {
    if (columnVisibility && Object.keys(columnVisibility)?.length > 0) {
      const patientTableColumnKey = `patientTableColumn:${currentUser?.userId}`;
      localStorage.setItem(
        patientTableColumnKey,
        JSON.stringify(columnVisibility)
      );
    }
  }, [columnVisibility]);

  useEffect(() => {
    try {
      if (currentUser?.userId) {
        const patientTableColumnKey = `patientTableColumn:${currentUser?.userId}`;

        const savedVisibility =
          JSON.parse(localStorage.getItem(patientTableColumnKey)) || {};

        // Always-on columns stay on even if an older saved layout hid them.
        columns.forEach((column) => {
          if (column?.enableHiding && column?.accessorKey) {
            savedVisibility[column.accessorKey] = true;
          }
        });

        setColumnVisibility(savedVisibility);
      }
    } catch (err) {
      console.error(err);
    }
  }, [currentUser]);

  return (
    <>
      <CustomTable
        columns={columns}
        data={data?.data || []}
        searchText={searchText}
        options={{
          enableTopToolbar: true,
          enableSorting: true,
          sortDescFirst: false,
          rowCount: data?.meta?.total || 0,
          enableRowVirtualization: true,
          enableRowSelection: [USER_ROLES.ADMIN].includes(currentUser?.role),
          defaultColumn: {
            minSize: 20,
            maxSize: 9001,
            size: 170,
          },
          muiSearchTextFieldProps: {
            placeholder: "Search Patient",
            onPointerCancel: () => {
              onSearchChange("");
            },
          },
          state: {
            isLoading: isLoading || isFetching,
            showGlobalFilter: true,
            showColumnFilters: true,
            columnFilters,
            globalFilter: search,
            pagination,
            sorting,
            columnVisibility,
            rowSelection,
          },
          onColumnVisibilityChange: setColumnVisibility,
          manualPagination: true,
          manualFiltering: true,
          manualSorting: true,
          onGlobalFilterChange: onSearchChange,
          onPaginationChange: setPagination,
          onColumnFiltersChange: setColumnFilters,
          onSortingChange: handleSortingChange,
          getRowId: (row) => row._id,
          onRowSelectionChange: setRowSelection,
          onSelectionChange: (rows) => {
            const patients = rows?.map(({ _id }) => _id);
            dispatch(
              changeSelectedRows({ patients, selectedRows: rows?.length })
            );
          },
          rowsPerPageOptions: DEFAULT_LARGE_PAGE_SIZE_OPTIONS,
          renderEmptyRowsFallback: () => undefined,
          muiTableBodyRowProps: ({ row }) => ({
            onClick: () => onRowClick(row?.original),
            sx: {
              cursor: "pointer",
              "&.MuiTableRow-root.Mui-selected td:after": {
                background: "none !important",
              },
              "td:nth-of-type(3)": {
                paddingLeft: "16px",
                paddingRight: "16px",
              },
              "td:nth-of-type(3):hover": {
                background: "rgba(0, 0, 0, 0.05)",
              },
            },
          }),
          renderTopToolbar: ({ table }) => (
            <PatientTableToolbar
              table={table}
              sorting={sorting}
              filters={columnFilters}
            />
          ),
        }}
      />

      <PatientBulkActionModal
        setRowSelection={setRowSelection}
        leadModel={
          columnFilters?.filter(({ id }) => id === "leadModel")?.[0]?.value
        }
        selectedPatients={
          data?.data?.filter(({ _id }) => rowSelection[_id]) || []
        }
      />
    </>
  );
};

export default PatientsTable;
